import router from "@/router";
import store from "@/store";
const provider = new OpenStreetMapProvider();
import { OpenStreetMapProvider } from "leaflet-geosearch";
import axios from "axios";
import { isEqual } from "lodash";
import Vue from "vue";

const env = require(`@/environments/${process.env.VUE_APP_ENV_PATH}`);

export function getTokenHeader() {
  const sessionString = localStorage.getItem("session");
  if (canParseJson(sessionString)) {
    const token = JSON.parse(sessionString)?.token;
    if (token) return { Authorization: `Bearer ${token}` };
    else return false;
  }
}

export function canParseJson(json) {
  try {
    JSON.parse(json);
    return true;
  } catch (ex) {
    return false;
  }
}

export function changeItemSelection(module, isSub = false) {
  store.state.module = module;
  store.state.modules.forEach(item => {
    item.selected = item.id === module.id;
    item.sub_items?.forEach(sub => {
      sub.selected =
        sub.path.includes(item.path) && sub.path === module.path && isSub;
      if (sub.selected) item.selected = true;
    });
  });
  if (router.currentRoute.path !== module.path) {
    router.push(module.path);
  }
}

export async function getCoordinatesByAddress(
  addressObj,
  optionDialog,
  tableOptions
) {
  const { zip, city, address } = addressObj;
  // Stadt und PLZ sind von der Search Funktion vorgegeben
  if (zip && city) {
    let addressString = `${zip}, ${city}`;
    // Falls Straße und Hausnummer verfügbar Suche verfeinern
    if (address) addressString += `, ${address}`;

    // Anfrage an den Location Provider
    const res = await provider.search({ query: addressString });
    if (res) {
      // Wenn nur ein Standort gefunden wurde,
      // wird dieser Standort einfach festgelegt
      if (res.length === 1) {
        const { x, y } = res[0];
        return { status: true, geo: { lng: x, lat: y } };
        // Wenn mehr als ein Standort gefunden wurde,
        // bekommt der User eine Auswahl um den Standort genauer zu bestimmen
      } else if (res.length) {
        // Ergebnis in das richtige Format bringen
        const options = res.map(r => ({
          lng: r.x,
          lat: r.y,
          displayName: r.label,
          osmId: r.raw.osm_id
        }));

        // tableOptions ist eine Vorgabe wie der Dialog am Ende aussehen soll.
        optionDialog?.showDialog(options, tableOptions);
      } else {
        return { status: false, message: "Keine Ergebnisse gefunden" };
      }
    } else {
      return { status: false, message: "Keine Ergebnisse gefunden" };
    }
  } else {
    return {
      status: false,
      message: "Zum Berechnen der Koordinaten werden PLZ und Stadt benötigt!"
    };
  }
}

export function forceFileDownload(data, title) {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", title);
  document.body.appendChild(link);
  link.click();
}

export function getThumbnailUrl(url, module, width = 200, height = 200) {
  if (!url?.length) {
    return `${require("@/assets/images/imagePlaceholder.svg")}`;
  }

  const canBeCroppedOrResized = [".gif", ".pdf"].every(
    suffix => !url.endsWith(suffix)
  );
  if (!canBeCroppedOrResized) {
    return url;
  }

  const isEoFile = [
    "eo-file",
    "download.eberl-online",
    "download-test.eberl-online",
    "download.eo-heimat",
    "download-test.eo-heimat"
  ].some(_ => url.includes(_));
  if (isEoFile) {
    //example: https://eo-file.hydra.eberl-online.net/news/5e201aa71ca7e95f0c72d274/biceps.jpg
    const regex = new RegExp(`(.+//.+)/(${module}/.+)`);
    const match = regex.exec(url);
    const domain = match[1];
    const sizeString = `/t/${width}x${height}/`;
    const fileString = match[2];
    //example: https://eo-file.hydra.eberl-online.net/t/520x520/news/5e201aa71ca7e95f0c72d274/biceps.jpg
    return encodeURI(domain + sizeString + fileString);
  }

  const isOutdoorActiveFile = url.includes("oastatic.com");
  if (isOutdoorActiveFile) {
    return encodeURI(url.replace("/img/", `/img/${width}/${height}/`));
  }

  return `${require("@/assets/images/imagePlaceholder.svg")}`;
}

export function getImageSize(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.src = src;
    image.onload = function() {
      resolve({ width: this.width, height: this.height });
    };
    image.onerror = reject;
  });
}

export async function getImageDimensionSizes(imageUrl) {
  const sizes = await getImageSize(imageUrl);
  const { width, height } = sizes;
  const urlArrayDesktop = imageUrl.split("/");
  const urlArrayTablet = imageUrl.split("/");
  const urlArrayMobile = imageUrl.split("/");

  const heightDesktop = Math.min(Math.floor(width / (16 / 9)), height);
  const heightTablet = Math.min(Math.floor(width / 3), height);
  const sizeMobile = Math.floor(width > height ? height : width);

  urlArrayDesktop.splice(3, 0, `focus/0/0/${width}/${heightDesktop}`);
  const desktopUrl = urlArrayDesktop.join("/");

  urlArrayTablet.splice(3, 0, `focus/0/0/${width}/${heightTablet}`);
  const tabletUrl = urlArrayTablet.join("/");

  urlArrayMobile.splice(3, 0, `focus/0/0/${sizeMobile}/${sizeMobile}`);
  const mobileUrl = urlArrayMobile.join("/");

  return {
    "16:9": {
      url: desktopUrl,
      left: 0,
      right: 0,
      top: 0,
      bottom: parseInt(height - heightDesktop),
      width: width,
      height: heightDesktop
    },
    "3:1": {
      url: tabletUrl
    },
    "1:1": {
      url: mobileUrl
    }
  };
}

export async function destroyFile(bucket, fileId) {
  const env = require(`@/environments/${process.env.VUE_APP_ENV_PATH}`);
  const headers = getTokenHeader();
  const token = env.file_client_token;
  try {
    await axios.delete(`${env.file_client_url}v1/file/destroy`, {
      params: {
        token,
        bucket,
        id: fileId
      },
      headers
    });
    return true;
  } catch (e) {
    console.error(`can't delete file (id: ${fileId})`, e);
    return false;
  }
}

export function getMongoFileId(file) {
  //extract file-id from file-url
  const filename = file.url.split("/").pop();
  const fileNameSplit = filename.split(".");
  fileNameSplit.pop();
  return fileNameSplit.join(".");
}

export function getPictureUrlDatePath(url) {
  const regex = /\d{4}\/\d{4}-\d{2}-\d{2}\//;
  const match = url.match(regex)?.[0];
  return match ?? "";
}

// copy file. returns new id and url
export async function copyFileById(oldId, oldPictureDatePath, createError) {
  const env = require(`@/environments/${process.env.VUE_APP_ENV_PATH}`);
  try {
    const headers = getTokenHeader();
    const res = await axios.post(
      `${env.file_client_url}v1/file/copy`,
      {
        id: oldId,
        datePath: oldPictureDatePath
      },
      { headers }
    );
    return {
      id: res.data?.data?._doc?._id,
      url: res.data?.url
    };
  } catch (err) {
    if (createError) createError(err);
    return false;
  }
}

export function escapeHTML(html) {
  if (!html?.length) {
    return "";
  }
  const t = document.createTextNode(html);
  const p = document.createElement("p");
  p.appendChild(t);
  return p.innerHTML;
}

export function initGlobalCopyToClipboard() {
  // Function(s) needs to be available in global context, independent of Vue.
  if (window.copyCodePreviewToClipboard) {
    return;
  }
  window.alertCopySuccessful = () =>
    this.$store.dispatch(
      "createSuccess",
      "Text erfolgreich in die Zwischenablage kopiert!"
    );
  window.alertCopyError = () =>
    this.$store.dispatch("createError", {
      text: "Text konnte nicht in die Zwischenablage kopiert werden!"
    });
  window.copyCodePreviewToClipboard = function(codePreviewId) {
    const code = document.getElementById(codePreviewId)?.innerText;
    if (code) {
      navigator.clipboard
        .writeText(code)
        .then(window.alertCopySuccessful)
        .catch(window.alertCopyError);
    } else {
      window.alertCopyError();
    }
  };
}

export function getPrincipalName(principal) {
  const foundPrincipal = this.$store.state.principals.find(
    p => p._id === principal
  );
  if (foundPrincipal?._id) return foundPrincipal.name || "";
  return "";
}

export function getApiKeyDescription(apiKey) {
  const eventConfig = this.$store.state.eventConfig;
  const key = eventConfig?.apiKeys?.find(k => k.apiKey === apiKey);
  return key?.description ?? "";
}

export function reportError(error) {
  const url = env.news_api_client_url + "reporter/";
  console.error(error);

  const currentUrl = new URL(window.location.href);
  if (!currentUrl?.host?.includes("eo-heimat.de")) return;

  axios
    .post(url, {
      name: error.name,
      message: error.message,
      stack: error.stack
    })
    .catch(err => {
      console.error("Failed to send error:", err);
    });
}

export function getThirdPartySystem(typeIds, module) {
  const ret = [];
  if (!typeIds?.length) return ret;
  for (const typeId of typeIds) {
    const system = this.$store.state.thirdPartySystems.find(
      system =>
        system.typeId === typeId &&
        system.modules.find(_module => _module.type === module)
    );
    if (system) {
      ret.push({
        name: system.name,
        description: system.modules
          .find(_module => _module.type === module)
          .description.replace(/[\r\n]/g, "<br/>")
      });
    }
  }

  return ret;
}

function getOrCreateCanvas() {
  const canvasId = "pdfToImageCanvas";
  let canvas = document.getElementById(canvasId);
  if (canvas) {
    return canvas;
  }
  canvas = document.createElement("canvas");
  canvas.id = canvasId;
  canvas.style.display = "none";
  document.body.appendChild(canvas);
  return canvas;
}

export async function generateImageFromPdf(url, title) {
  // See example https://mozilla.github.io/pdf.js/examples/
  const loadingTask = window.pdfjsLib.getDocument(url);
  const pdf = await loadingTask.promise;
  const page = await pdf.getPage(1);

  const scale = 1.5;
  const viewport = page.getViewport({ scale: scale });

  // Support HiDPI-screens.
  const outputScale = window.devicePixelRatio || 1;

  const canvas = getOrCreateCanvas();
  const context = canvas.getContext("2d");

  canvas.width = Math.floor(viewport.width * outputScale);
  canvas.height = Math.floor(viewport.height * outputScale);
  canvas.style.width = Math.floor(viewport.width) + "px";
  canvas.style.height = Math.floor(viewport.height) + "px";

  const transform =
    outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null;

  const renderContext = {
    canvasContext: context,
    transform: transform,
    viewport: viewport
  };
  await page.render(renderContext).promise;

  const fullQuality = canvas.toDataURL("image/jpeg", 1.0);
  const file = await fetch(fullQuality)
    .then(res => res.blob())
    .then(
      blob =>
        new File([blob], `${title}.jpg`, {
          type: "image/jpeg"
        })
    );
  return file;
}

export function isEmptyValue(value) {
  if (!value) {
    return true;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return true;
    }
    return value.every(_value => isEmptyValue(_value));
  }
  if (typeof value === "object") {
    const entries = Object.entries(value);
    if (entries.length === 0) {
      return true;
    }
    return entries.every(([, _value]) => isEmptyValue(_value));
  }
  return false;
}

export function getObjectDiff(obj1, obj2) {
  const diff = Object.keys(obj1).reduce((result, key) => {
    if (!Object.hasOwn(obj2, key)) {
      result.push(key);
    } else if (isEqual(obj1[key], obj2[key])) {
      const resultKeyIndex = result.indexOf(key);
      result.splice(resultKeyIndex, 1);
    }
    return result;
  }, Object.keys(obj2));

  return diff;
}

export function getIsOwnedBySubPrincipalWarningHTML(principalName) {
  const isOwnedBySubprincipalHTML = `           
    <p class="font-weight-bold">
        ACHTUNG - Sie sind nicht Eigentümer/Ersteller dieses Datensatzes (Eigentümer = ${principalName})
    </p>
    <p>
        Bitte beachten Sie, dass Änderungen an den Eigenschaften auch Einfluss auf die Ausgabekanäle des Eigentümers hat.
        Bitte nutzen Sie lediglich eigene Beschreibungstexte oder Eigene Merkmale!
        Bitte verändern Sie nicht ohne Rücksprache mit dem Eigentümer Bilder da diese ggf. Nutzungsrechte verletzen.
    </p>
`;
  return isOwnedBySubprincipalHTML;
}

export function onCKEditorReady() {
  window._ckeditorReady = true;
}

export function setCKEditorPrincipalColorPalette() {
  const url = new URL(window.location);
  const regex = /^\/(.+?)(\/.*)?$/;
  const currentModule = url.pathname.match(regex)?.[1] ?? "";

  const colorPalette =
    store.state.selectedPrincipal?.colorPalette?.[currentModule] ?? {};

  const colors = Object.entries(colorPalette)
    .filter(([, value]) => Array.isArray(value.colors))
    .map(([, value]) => value.colors)
    .flat();

  const uniqueColors = colors.reduce((acc, curr) => {
    if (!acc.some(c => c.color === curr.value)) {
      acc.push({ color: curr.value, label: curr.label });
    }
    return acc;
  }, []);

  Vue.prototype.$editorConfig.fontColor = {
    colors: uniqueColors
  };
  Vue.prototype.$editorConfig.fontBackgroundColor = {
    colors: uniqueColors
  };
}

export function setCKEditorSourceEditing() {
  const isAdmin = store.state.currentUser?.scopes?.includes("role:admin");
  const sourceButton = "sourceEditing";
  if (isAdmin) {
    if (!Vue.prototype.$editorConfig.toolbar.includes(sourceButton)) {
      Vue.prototype.$editorConfig.toolbar.push("|", sourceButton);
    }
  } else {
    const index = Vue.prototype.$editorConfig.toolbar.indexOf(sourceButton);
    if (index > -1) {
      Vue.prototype.$editorConfig.toolbar.splice(index, 1);
    }
  }
}

export const aiAttributionItems = [
  {
    title: "keine Zuordnung",
    value: ""
  },
  {
    title: "von Menschen erstellt",
    value: "human_created"
  },
  {
    title: "von Menschen erstellt, mit KI überarbeitet",
    value: "human_created_ai_edited"
  },
  {
    title: "KI-generiert, menschlich geprüft",
    value: "ai_generated_human_verified"
  },
  {
    title: "KI-generiert",
    value: "ai_generated"
  }
];
